import React, { useEffect, useState } from "react";
import Image from "next/image";
import Link from "next/link";
import { Breadcrumb } from "antd";
import styles from "@aspen/theme/Profile.module.less";
import type { IkycInfo } from "@aspen/model";
import { KYC_STATUS, KYC_TYPE_QUERY } from "@aspen/model";
import { i18nUtil, PROFILE_PATHS } from "@aspen/libs";
import { WithKYCInfo } from "@aspen/ui";
import ic_get from "@aspen/assets/images/ic_get@2x.png";
import ic_review from "@aspen/assets/images/underview.svg";
import ic_reject from "@aspen/assets/images/reject.svg";
import {
  KYC_INDIVIDUALS_DISPLAY_KEYS,
  KYC_INDIVIDUALS_LABEL_TEXT,
  KYC_INSTITUTIONS_DISPLAY_KEYS,
  KYC_INSTITUTIONS_LABEL_TEXT,
} from "@adgm/cross-models";

const STATUS_ICON_MAP = {
  Auditing: ic_review,
  Rejected: ic_reject,
  Certified: ic_get,
};

const STATUS_TEXT_MAP = {
  Auditing: "kyc.status.under.review",
  Rejected: "kyc.rejected.text",
  Certified: "kyc.certified.text",
};

interface IProps {
  kycInfo: IkycInfo;
  getKYCInfo: () => void;
}

interface QuestionnaireItem {
  label: string;
  name: string;
}

const Result: React.FC<IProps> = props => {
  const { kycInfo } = props;
  const intl = i18nUtil.t();
  const displayFieldKeys =
    kycInfo.type === KYC_TYPE_QUERY.institutions
      ? KYC_INSTITUTIONS_DISPLAY_KEYS
      : KYC_INDIVIDUALS_DISPLAY_KEYS;

  const [questionnaireArray, setQuestionnaireArray] = useState<QuestionnaireItem[]>([]);

  const statusText = KYC_STATUS[kycInfo.status ?? 1];
  const icon_status = STATUS_ICON_MAP[statusText] ?? ic_get;
  const text_status = intl[STATUS_TEXT_MAP[statusText] ?? "kyc.certified.text"];

  useEffect(() => {
    props.getKYCInfo();
  }, []);

  useEffect(() => {
    const questionnaireIndividual = intl["kyc.individual.questionnaire"];
    const questionnaireInstitutions = intl["kyc.institutions.questionnaire"];
    const questionnaireCommon = intl["kyc.common.questionnaire"];
    if (kycInfo.type === KYC_TYPE_QUERY.institutions) {
      setQuestionnaireArray([...questionnaireInstitutions, ...questionnaireCommon]);
    } else {
      setQuestionnaireArray([...questionnaireIndividual, ...questionnaireCommon]);
    }
  }, [kycInfo.type]);

  /**
   * 渲染KYC信息表格行
   *
   * 该函数接收一个来自 IkycInfo 接口的键值作为参数，
   * 从 kycInfo 对象中提取对应的标签和值，
   * 并渲染成一个包含标签和值的表格行
   *
   * @param {string} key - IkycInfo 接口中的键名
   * @returns {ReactNode} - 返回一个包含标签和值的 div 元素，如果值为空则返回 null
   */
  const renderTableRow = (key: string) => {
    // 从国际化文本中获取对应的标签文本
    const labelMap =
      kycInfo.type === KYC_TYPE_QUERY.institutions
        ? KYC_INSTITUTIONS_LABEL_TEXT
        : KYC_INDIVIDUALS_LABEL_TEXT;
    const label = intl[labelMap[key]];

    let value = kycInfo[key];

    // 如果键名以 director 或 beneficiary 开头，
    // 则从对应的 director 或 beneficiary 对象中提取值
    if (key.startsWith("director") || key.startsWith("beneficiary")) {
      const prefix = key.startsWith("director") ? "director" : "beneficiary";
      // 处理字段名，移除前缀并将首字母转为小写
      const field =
        key.replace(prefix, "").charAt(0).toLowerCase() + key.replace(prefix, "").slice(1);
      value = kycInfo[prefix]?.[field];
    }

    // 只有当值存在时才渲染表格行
    return value ? (
      <div className={styles.item} key={key}>
        <div className={styles.inner}>
          <p className={styles.label}>{label}</p>
          <p className={styles.value}>{value}</p>
        </div>
      </div>
    ) : null;
  };

  const renderQuestionnaireRow = (list: QuestionnaireItem[]) => {
    return list.map(item => {
      const { label, name } = item;
      const value = kycInfo[name];
      return (
        <div
          className={styles.item}
          key={name}
          style={{ display: value ? "inline-block" : "none" }}>
          <div className={styles.inner}>
            <p className={styles.label}>{label}</p>
            <p className={styles.value}>{value}</p>
          </div>
        </div>
      );
    });
  };

  return (
    <>
      <Breadcrumb>
        <Breadcrumb.Item>{intl["kyc.certify.location"]}:</Breadcrumb.Item>
        <Breadcrumb.Item>
          <Link href={PROFILE_PATHS.PROFILE}>{intl["profile"]}</Link>
        </Breadcrumb.Item>
        <Breadcrumb.Item>{intl["kyc.information.title"]}</Breadcrumb.Item>
      </Breadcrumb>
      <div className={styles.kycResult}>
        <p className={styles.title}>{intl["kyc.information.title"]}</p>
        <div className={styles.kycStatus}>
          <span style={{ marginRight: 5 }}>{intl["kyc.status.under.status"]}:</span>
          <Image unoptimized src={icon_status} alt="" width={16} height={16} />
          {kycInfo?.status && (
            <span
              className={styles[KYC_STATUS[kycInfo.status]?.toLowerCase()]}
              style={{ marginLeft: 5 }}>
              {text_status}
            </span>
          )}
        </div>
        <div className={styles.kycTable}>
          {displayFieldKeys.map((key: string) => renderTableRow(key))}
          {renderQuestionnaireRow(questionnaireArray)}
        </div>
      </div>
    </>
  );
};

export const PageKycInformation = WithKYCInfo(Result);
